import { PlusIcon } from "@heroicons/react/20/solid";
import { useI18nextInstance } from "@squide/i18next";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button.tsx";
import { Popover, PopoverContent } from "@/components/ui/popover.tsx";
import type { TenureBucketKey } from "#shared/reviewee-filters/tenureBuckets.ts";
import type { TrackOption } from "#shared/reviewee-filters/trackOptions.ts";
import { reviewCyclesI18NextInstanceKey } from "../../i18n/i18next.ts";
import { type FilterKind, FilterKinds } from "./filterKinds.ts";
import { FilterKindList } from "./FilterKindList.tsx";
import { DepartmentFilterPanel } from "./DepartmentFilterPanel.tsx";
import { GenderFilterPanel } from "./GenderFilterPanel.tsx";
import { JobTitleFilterPanel } from "./JobTitleFilterPanel.tsx";
import { DirectManagersFilterPanel } from "./DirectManagersFilterPanel.tsx";
import { ReportingLineFilterPanel } from "./ReportingLineFilterPanel.tsx";
import { RoleLevelFilterPanel } from "./RoleLevelFilterPanel.tsx";
import { SegmentFilterPanel } from "./SegmentFilterPanel.tsx";
import { TenureFilterPanel } from "./TenureFilterPanel.tsx";
import { TrackFilterPanel } from "./TrackFilterPanel.tsx";
import { WorkLocationFilterPanel } from "./WorkLocationFilterPanel.tsx";

export interface AddFilterButtonProps {
    selectedManagerIds: string[];
    onManagerSelectionChange: (managerIds: string[]) => void;
    selectedReportingLineIds: string[];
    onReportingLineSelectionChange: (reportingLineIds: string[]) => void;
    selectedSegmentIds: string[];
    onSegmentSelectionChange: (segmentIds: string[]) => void;
    selectedDepartments: string[];
    onDepartmentSelectionChange: (departments: string[]) => void;
    selectedGenders: string[];
    onGenderSelectionChange: (genders: string[]) => void;
    selectedWorkLocations: string[];
    onWorkLocationSelectionChange: (workLocations: string[]) => void;
    selectedJobLevels: string[];
    onJobLevelSelectionChange: (jobLevels: string[]) => void;
    selectedJobTitles: string[];
    onJobTitleSelectionChange: (jobTitles: string[]) => void;
    selectedTenure: TenureBucketKey | null;
    onTenureSelectionChange: (tenure: TenureBucketKey | null) => void;
    selectedTrack: TrackOption | null;
    onTrackSelectionChange: (track: TrackOption | null) => void;
}

export function AddFilterButton({
    selectedManagerIds,
    onManagerSelectionChange,
    selectedReportingLineIds,
    onReportingLineSelectionChange,
    selectedSegmentIds,
    onSegmentSelectionChange,
    selectedDepartments,
    onDepartmentSelectionChange,
    selectedGenders,
    onGenderSelectionChange,
    selectedWorkLocations,
    onWorkLocationSelectionChange,
    selectedJobLevels,
    onJobLevelSelectionChange,
    selectedJobTitles,
    onJobTitleSelectionChange,
    selectedTenure,
    onTenureSelectionChange,
    selectedTrack,
    onTrackSelectionChange
}: AddFilterButtonProps) {
    const i18nextInstance = useI18nextInstance(reviewCyclesI18NextInstanceKey);
    const { t } = useTranslation("AddFilterButton", { i18n: i18nextInstance });
    const { t: tKinds } = useTranslation("FilterKinds", { i18n: i18nextInstance });
    const [openFilter, setOpenFilter] = useState<FilterKind | null>(null);

    const handleOpenChange = useCallback((isOpen: boolean) => {
        if (!isOpen) {
            setOpenFilter(null);
        }
    }, []);

    const handleBackToList = useCallback(() => setOpenFilter(null), []);

    const appliedKinds = useMemo(() => {
        const isApplied: Record<FilterKind, boolean> = {
            directManagers: selectedManagerIds.length > 0,
            reportingLine: selectedReportingLineIds.length > 0,
            segment: selectedSegmentIds.length > 0,
            department: selectedDepartments.length > 0,
            gender: selectedGenders.length > 0,
            workLocation: selectedWorkLocations.length > 0,
            roleLevel: selectedJobLevels.length > 0,
            jobTitle: selectedJobTitles.length > 0,
            tenure: Boolean(selectedTenure),
            track: Boolean(selectedTrack)
        };

        return new Set(FilterKinds.filter(kind => isApplied[kind]));
    }, [selectedManagerIds, selectedReportingLineIds, selectedSegmentIds, selectedDepartments, selectedGenders, selectedWorkLocations, selectedJobLevels, selectedJobTitles, selectedTenure, selectedTrack]);

    const tenureBuckets = useMemo(() => (selectedTenure ? [selectedTenure] : []), [selectedTenure]);
    const tracks = useMemo(() => (selectedTrack ? [selectedTrack] : []), [selectedTrack]);

    const handleTenureChange = useCallback((next: TenureBucketKey[]) => onTenureSelectionChange(next[0] ?? null), [onTenureSelectionChange]);
    const handleTrackChange = useCallback((next: TrackOption[]) => onTrackSelectionChange(next[0] ?? null), [onTrackSelectionChange]);

    return (
        <Popover onOpenChange={handleOpenChange}>
            <Button intent="outline" size="sm" className="h-9">
                <PlusIcon aria-hidden="true" />
                {t("label")}
            </Button>
            <PopoverContent placement="bottom start" aria-label={t("label")} className="bg-bg p-3 [&_[data-slot=popover-inner]]:overflow-x-hidden">
                {openFilter === "directManagers" ? (
                    <DirectManagersFilterPanel selectedIds={selectedManagerIds} onSelectionChange={onManagerSelectionChange} onBack={handleBackToList} />
                ) : openFilter === "reportingLine" ? (
                    <ReportingLineFilterPanel selectedIds={selectedReportingLineIds} onSelectionChange={onReportingLineSelectionChange} onBack={handleBackToList} />
                ) : openFilter === "segment" ? (
                    <SegmentFilterPanel selectedIds={selectedSegmentIds} onSelectionChange={onSegmentSelectionChange} onBack={handleBackToList} />
                ) : openFilter === "department" ? (
                    <DepartmentFilterPanel selectedValues={selectedDepartments} onSelectionChange={onDepartmentSelectionChange} onBack={handleBackToList} />
                ) : openFilter === "gender" ? (
                    <GenderFilterPanel selectedValues={selectedGenders} onSelectionChange={onGenderSelectionChange} onBack={handleBackToList} />
                ) : openFilter === "workLocation" ? (
                    <WorkLocationFilterPanel selectedValues={selectedWorkLocations} onSelectionChange={onWorkLocationSelectionChange} onBack={handleBackToList} />
                ) : openFilter === "roleLevel" ? (
                    <RoleLevelFilterPanel selectedValues={selectedJobLevels} onSelectionChange={onJobLevelSelectionChange} onBack={handleBackToList} />
                ) : openFilter === "jobTitle" ? (
                    <JobTitleFilterPanel selectedValues={selectedJobTitles} onSelectionChange={onJobTitleSelectionChange} onBack={handleBackToList} />
                ) : openFilter === "tenure" ? (
                    <TenureFilterPanel selectedBuckets={tenureBuckets} onSelectionChange={handleTenureChange} onBack={handleBackToList} />
                ) : openFilter === "track" ? (
                    <TrackFilterPanel selectedTracks={tracks} onSelectionChange={handleTrackChange} onBack={handleBackToList} />
                ) : (
                    <FilterKindList ariaLabel={t("label")} kinds={FilterKinds} appliedKinds={appliedKinds} getKindLabel={tKinds} appliedLabel={tKinds("applied")} onSelect={setOpenFilter} className="w-64" />
                )}
            </PopoverContent>
        </Popover>
    );
}
